import React, { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { SelectPagination } from "@foris/avocado-suite";
import { orderBy } from "lodash";
import { groupLabels } from "../graphql/groupLabels.query";
import { ContextEditLabel, ILabel } from "./ContextEditLabels";
import { IParams } from "@models/IParams";
import { LabelPage, PageInfo, Label } from "@models/ISchema";
import styles from "./selectLabels.module.scss";

interface LabelOption {
  label: string;
  value: ILabel;
}

interface SelectLabelsProps {
  className?: string;
  contentType?: "group" | "package" | "all";
  label?: string;
  labelCategoryId?: string;
  plaholder?: string;
  isLabelDisabled?: (label: Label) => boolean;
  onSelectLabel?: (label: LabelOption) => void;
}

const SelectLabels = ({
  className = "col_3",
  contentType = "group",
  labelCategoryId,
  label,
  plaholder,
  isLabelDisabled,
  onSelectLabel,
}: SelectLabelsProps) => {
  // Hooks for handle the state of the component
  const context = useContext(ContextEditLabel);
  const [options, setOptions] = useState<LabelOption[]>([]);
  const client = useApolloClient();
  const { scenario }: IParams = useParams();
  const { t } = useTranslation();
  // State
  const [termToSearch, setTermToSearch] = useState("");
  const [groupsLabelsPage, setGroupsLabelsPage] = useState(1);

  const defaultPlaceholder = t("edit-tag-manually.select-option.placeholder");

  const getGroupLabels = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;

      try {
        const variables = {
          query: groupLabels,
          variables: {
            scenarioId: scenario,
            filter: {
              fields: {
                contentType:
                  contentType && contentType !== "all"
                    ? {
                        eq: contentType,
                      }
                    : undefined,
                labelSourceCode: {
                  // Only get labels that can be selected by the user
                  eq: "USER-DEFINED",
                },
                labelCategoryId: labelCategoryId
                  ? {
                      eq: labelCategoryId,
                    }
                  : undefined,
              },
              pagination: {
                page,
                size: optionsPerPage,
                searchTerm,
              },
            },
          },
        };
        const { data } = await client.query(variables);
        const labels: LabelPage = data?.data?.labels;
        const pageInfo: PageInfo = data?.data?.labels?.pageInfo;
        const options = [];
        if (labels) {
          labels.items.forEach(item => {
            const option = {
              label: `${item?.code} | ${item?.name} | ${item?.category?.name}`,
              value: { ...item, isNew: true },
            };
            options.push(option);
          });
        }
        setOptions(options);

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [client],
  );

  const labelsLoadOptions = async (searchTerm: string) => {
    let searchPage = 1;
    if (termToSearch === searchTerm) {
      searchPage = groupsLabelsPage;
    } else {
      setTermToSearch(searchTerm);
    }
    const { pageInfo, options: groupsLabelsOptions } = await getGroupLabels(searchTerm, searchPage);
    setGroupsLabelsPage(groupsLabelsPage + 1);
    return {
      options: groupsLabelsOptions,
      hasMore: pageInfo?.hasNextPage,
      additional: {
        page: groupsLabelsPage,
      },
    };
  };

  useEffect(() => {
    getGroupLabels();
  }, []);

  const orderOptions = useMemo(() => {
    return orderBy(options, ["value.code"], ["asc"]);
  }, [options]);

  const selectedLabelIds = useMemo(() => {
    const labelsIdsMap = new Map();

    context?.labels?.forEach(label => {
      labelsIdsMap.set(label.id, true);
    });

    return labelsIdsMap;
  }, [context?.labels]);

  const customStyles = {
    option: (styles: any) => {
      return {
        ...styles,
        color: "#6a7075",
      };
    },
  };

  return (
    <SelectPagination
      label={label}
      className={className}
      placeholder={plaholder ?? defaultPlaceholder}
      loadOptions={labelsLoadOptions}
      options={orderOptions}
      styles={customStyles}
      noOptionsMessage={() => (
        <>{t("advanced-search.package.forms.advanced-filters.select-tag.empty-options")}</>
      )}
      value={null}
      isOptionDisabled={(option: any) => {
        return isLabelDisabled?.(option.value) || selectedLabelIds?.has(option.value.id);
      }}
      formatOptionLabel={(option: any) => {
        const isDisabledByRule = isLabelDisabled?.(option.value);
        const isAlreadySelected = selectedLabelIds?.has(option.value.id);

        if (!isDisabledByRule || isAlreadySelected) {
          return option.label;
        }

        return (
          <span className={styles.selectLabels_option}>
            <span>{option.label}</span>
            <span className={styles.selectLabels_notAssignable}>
              {t("edit-tag-manually.select-option.not-assignable")}
            </span>
          </span>
        );
      }}
      onChange={(selectedLabel: LabelOption) => {
        if (onSelectLabel) {
          return onSelectLabel(selectedLabel);
        }

        context.setLabels([...context.labels, selectedLabel.value]);
      }}
    />
  );
};

export default SelectLabels;
